import * as React from 'react';

import { cn, TextField, PhotoCard } from '@rematter/ui-web';

import { GenericFile } from '~/modules/trade/types/file';
import { getFileDisplayState } from '~/modules/trade/utils/fileUpload';

const DEBOUNCE_DELAY_MS = 2000; // 2s

type OtherPhotoCardProps = {
  file: GenericFile;
  onView?: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onNotesChange?: (notes: string) => void;
  onRetry?: () => void;
  onDismissError?: () => void;
  isDownloading: boolean;
};

export const OtherPhotoCard: React.FC<OtherPhotoCardProps> = ({
  file,
  onView,
  onDownload,
  onDelete,
  onNotesChange,
  onRetry,
  onDismissError,
  isDownloading,
}) => {
  const { hasError, isPending, isUploading, displayImageUrl } =
    getFileDisplayState(file);

  const initialNotes = (file.metadata?.notes as string) || '';

  const [localNotes, setLocalNotes] = React.useState(initialNotes);
  const lastSavedNotesRef = React.useRef(initialNotes);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Sync local state when server data changes
  React.useEffect(() => {
    const serverNotes = (file.metadata?.notes as string) || '';
    if (serverNotes !== lastSavedNotesRef.current) {
      setLocalNotes(serverNotes);
      lastSavedNotesRef.current = serverNotes;
    }
  }, [file.metadata?.notes]);

  React.useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  const saveNotes = React.useCallback(
    (notes: string) => {
      if (notes !== lastSavedNotesRef.current) {
        lastSavedNotesRef.current = notes;
        onNotesChange?.(notes);
      }
    },
    [onNotesChange],
  );

  const handleNotesChange = React.useCallback(
    (value: string) => {
      setLocalNotes(value);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        saveNotes(value);
      }, DEBOUNCE_DELAY_MS);
    },
    [saveNotes],
  );

  const handleBlur = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    saveNotes(localNotes);
  }, [localNotes, saveNotes]);

  return (
    <div className={cn('@OtherPhotoCard flex flex-col')}>
      <PhotoCard
        imageUrl={displayImageUrl}
        fileName={file.fileName}
        timeStamp={file.updatedAt ?? file.createdAt}
        hasError={hasError}
        isUploading={isUploading || isPending}
        onView={onView}
        onDownload={onDownload}
        onDelete={onDelete}
        onRetry={onRetry}
        onDismissError={onDismissError}
        isDownloading={isDownloading}
      />

      <div className="mt-2">
        <TextField
          value={localNotes}
          onChange={handleNotesChange}
          onBlur={handleBlur}
          placeholder="Enter photo description..."
          isDisabled={isPending || hasError}
        />
      </div>
    </div>
  );
};
